import { JsonRpcProvider } from '@ethersproject/providers';
import { formatUnits } from '@ethersproject/units';
import { adaptEthersSigner } from '@reservoir0x/relay-ethers-wallet-adapter';
import Skeleton from 'react-loading-skeleton';
import { Constants, Currency } from '@manifoldxyz/js-ts-utils';
import { DEFAULT_STEP_STATE } from '@/common/constants';
import { TELEMETRY_EVENT_MAP } from '@/common/telemetry';
import {
  RequiredFund,
  StepConfig,
  TransactionStep,
  TransactionStepState,
  TxStepId,
  TxStepStatus,
} from '@/common/types';
import { getMissingFunds } from '@/common/util';
import { getTransactionErrorMessage } from '@/common/web3TransactionErrorHandling';
import PurchaseRequiredFundsRelayNetworkDropdown from '@/components/complete/relay/PurchaseRequiredFundsRelayNetworkDropdown';
import { useMissingFunds } from '@/hooks/useMissingFunds';
import { executeRelayTransaction } from '@/lib/relay';
import useMainStore, { StoreType } from '@/store/mainStore';

/**
 * Creates a step for reviewing and purchasing required ERC20 tokens
 * Allows users to see their current balance and purchase tokens if needed
 * @param requiredAmount - The fund requirement specifying token and amount needed
 * @param overrides - Optional overrides for step configuration and initial state
 * @returns A TransactionStep for fund review and purchase
 */
export async function createReviewFundsStep(
  requiredAmount: RequiredFund,
  overrides?: {
    config?: Partial<StepConfig>;
    state?: Partial<TransactionStepState>;
  },
): Promise<TransactionStep> {
  let name = 'Review ETH Balance';
  let primaryButtonText;
  const provider = window.ManifoldEthereumProvider.provider() as JsonRpcProvider;
  if (requiredAmount.address !== Constants.NULL_ADDRESS) {
    const erc20Metadata = await Currency.getERC20Metadata(
      requiredAmount.networkId,
      requiredAmount.address,
      // @ts-expect-error - provider is a JsonRpcProvider
      provider,
    );
    name = `Buy ${erc20Metadata.symbol}`;
    primaryButtonText = `Buy ${Currency.commifiedRoundedNumberString(
      formatUnits(requiredAmount.amount, erc20Metadata.decimals),
      4,
    )} ${erc20Metadata.symbol}`;
  }
  return {
    config: {
      id: TxStepId.ReviewERC20Balance,
      name,
      primaryButtonText,
      hideOnceCompleted: true,
      SecondaryComponent: ({ updateStepState }) => {
        const walletAddress = useMainStore((state) => state.walletAddress);

        const { missingFunds } = useMissingFunds({
          walletAddress: walletAddress,
          requiredFund: requiredAmount,
        });

        if (!missingFunds) {
          return <Skeleton height={68} />;
        }

        return (
          <PurchaseRequiredFundsRelayNetworkDropdown
            onNetworkSwitch={() => {
              updateStepState({ error: undefined });
            }}
            onError={(message: string) => {
              updateStepState({
                error: message,
              });
            }}
            missingFunds={missingFunds}
          />
        );
      },
      ...overrides?.config,
    },
    state: {
      ...DEFAULT_STEP_STATE,
      descriptionByStatus: {
        [TxStepStatus.Unstarted]: '',
        [TxStepStatus.Pending]: '',
        [TxStepStatus.Confirming]: '',
        [TxStepStatus.Confirmed]: '',
        [TxStepStatus.Failed]: '',
      },
      ...overrides?.state,
    },
    validateBefore: async (store) => {
      const studioInstance = store.studioInstance;
      const walletAddress = store.walletAddress;
      if (!studioInstance) {
        throw new Error('No studio instance');
      }
      if (!walletAddress) {
        throw new Error('No connected wallet address');
      }
      const missingFunds = await getMissingFunds(walletAddress, requiredAmount);

      // proceed to next step
      if (missingFunds.amount.isZero()) {
        return {
          skip: true,
        };
      }
    },

    execute: async (store: StoreType) => {
      const connectedNetwork = store.connectedNetwork;
      const studioInstance = store.studioInstance;
      const walletAddress = store.walletAddress;
      const analytics = store.analytics;
      if (!connectedNetwork) {
        throw new Error('No connected network');
      }
      if (!studioInstance) {
        throw new Error('No studio instance');
      }
      if (!walletAddress) {
        throw new Error('No connected wallet address');
      }

      const missingFunds = await getMissingFunds(walletAddress, requiredAmount);
      if (missingFunds.amount.isZero()) {
        return;
      }

      // Track the attempt of swap transaction
      analytics?.capture({
        event: TELEMETRY_EVENT_MAP.ERC20_SWAP_TX_ATTEMPT,
        properties: {
          tx_type: 'relay',
        },
      });

      try {
        const signer = window.ManifoldEthereumProvider.provider(true)?.getSigner();
        if (!signer) throw new Error('No signer found, unable to fetch quote');
        const wallet = adaptEthersSigner(signer);
        await executeRelayTransaction(connectedNetwork, studioInstance.publicData.network, {
          chainId: connectedNetwork,
          toChainId: studioInstance.publicData.network,
          currency: Constants.NULL_ADDRESS,
          toCurrency: requiredAmount.address,
          tradeType: 'EXACT_OUTPUT',
          amount: missingFunds.amount.toString(),
          recipient: walletAddress,
          wallet,
        });
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.ERC20_SWAP_TX_SUCCESS,
          properties: {
            tx_type: 'relay',
          },
        });
      } catch (error) {
        analytics?.capture({
          event: TELEMETRY_EVENT_MAP.ERC20_SWAP_TX_FAIL,
          properties: {
            tx_type: 'relay',
          },
        });
        throw new Error(getTransactionErrorMessage(error));
      }
    },
  };
}
